import type {
    EnhancedActionSlideGroup,
    EnhancedActionPlanSlide,
    EnhancedFactsSlide,
    EnhancedIssuesSlide,
    EnhancedNarrativeReport,
    EnhancedNarrativeSlide,
} from '../../interfaces/reports-tab/enhanced-narrative'

export type MaturityScore = { label: string; score: number | null; evidence: string }
export type NarrativeItem = { title: string; text: string }
export type ActionGroup = { timeframe: string; focus: string; items: NarrativeItem[] }
export type SpeakerNote = { layout: EnhancedNarrativeSlide['layout']; title: string; notes: string; sources: string[] }

export type EnhancedReportDocument = {
    report: EnhancedNarrativeReport
    reportHtml: string
    propertyName: string
    overviewTitle: string
    overviewSubtitle: string
    maturity: string
    summary: string
    strongestAssets: string[]
    primaryRisk: string
    confidenceLevel: string
    confidenceRationale: string
    radarTitle: string
    radarMin: number
    radarMax: number
    radarStep: number
    radarNote: string
    scores: MaturityScore[]
    issuesTitle: string
    issuesSubtitle: string
    keyIssues: NarrativeItem[]
    competitivePositioning: string
    responses: NarrativeItem[]
    planTitle: string
    planSubtitle: string
    actionGroups: ActionGroup[]
    targetOutcome: string
    sources: string[]
    speakerNotes: SpeakerNote[]
}

const cleanText = (value: unknown) => (typeof value === 'string' ? value : '').replace(/\s+/g, ' ').trim()
const stringList = (value: unknown) => (Array.isArray(value) ? value.map(cleanText).filter(Boolean) : [])
const safeUrl = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\//i.test(value)
const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value)

const parseJson = (value: unknown) => {
    if (typeof value !== 'string' || !value.trim().startsWith('{') && !value.trim().startsWith('[')) return null
    try {
        return JSON.parse(value) as unknown
    } catch {
        return null
    }
}

// Some API clients display the full HTML document with a backslash before its opening and
// closing tags. Accept that representation while leaving every other character untouched.
const readReportHtml = (value: unknown) => {
    if (typeof value !== 'string') return ''
    const html = value.trim().replace(/\\(?=<\/?html\b)/gi, '')
    return /<(?:!doctype\s+html|html|body)\b/i.test(html) ? html : ''
}

const findReportHtml = (value: unknown, depth = 0): string => {
    if (depth > 6) return ''
    const parsed = parseJson(value)
    if (parsed) return findReportHtml(parsed, depth + 1)
    if (Array.isArray(value)) {
        for (const entry of value) {
            const html = findReportHtml(entry, depth + 1)
            if (html) return html
        }
        return ''
    }
    if (!isRecord(value)) return ''
    const direct = readReportHtml(value.reportHtml)
    if (direct) return direct
    for (const nested of Object.values(value)) {
        const html = findReportHtml(nested, depth + 1)
        if (html) return html
    }
    return ''
}

// The layout key drives the deck, so infer it from the slide's own keys when the service omits it.
const layoutOf = (slide: Record<string, unknown>): EnhancedNarrativeSlide['layout'] | '' => {
    const layout = cleanText(slide.layout)
    if (layout === 'facts-and-radar' || layout === 'issues-and-competitors' || layout === 'three-horizon-action-plan') return layout
    if (slide.currentSituation || slide.radarChart) return 'facts-and-radar'
    if (slide.keyIssues || slide.competitorPositioning || slide.responses) return 'issues-and-competitors'
    if (slide.shortTerm || slide.midTerm || slide.longTerm) return 'three-horizon-action-plan'
    return ''
}

const readSlides = (value: unknown): EnhancedNarrativeSlide[] => {
    const entries = Array.isArray(value) ? value : Array.isArray(parseJson(value)) ? (parseJson(value) as unknown[]) : []
    return entries
        .filter(isRecord)
        .map((slide) => ({ ...slide, layout: layoutOf(slide) }))
        .filter((slide): slide is EnhancedNarrativeSlide & { layout: EnhancedNarrativeSlide['layout'] } => !!slide.layout)
}

// Accept the slide payload directly, stringified, or wrapped in any data/report/result envelope.
const findSlides = (value: unknown, depth = 0): { envelope: Record<string, unknown>; slides: EnhancedNarrativeSlide[] } | null => {
    if (depth > 6) return null
    const parsed = parseJson(value)
    if (parsed) return findSlides(parsed, depth + 1)
    if (Array.isArray(value)) {
        for (const entry of value) {
            const found = findSlides(entry, depth + 1)
            if (found) return found
        }
        return null
    }
    if (!isRecord(value)) return null
    const slides = readSlides(value.slides)
    if (slides.length) return { envelope: value, slides }
    for (const nested of Object.values(value)) {
        const found = findSlides(nested, depth + 1)
        if (found) return found
    }
    return null
}

export const normalizeEnhancedNarrativeResponse = (response: unknown): EnhancedNarrativeReport | null => {
    const found = findSlides(response)
    if (!found) return null
    const { envelope, slides } = found
    const reportHtml = findReportHtml(response)
    return {
        ...envelope,
        schemaVersion: typeof envelope.schemaVersion === 'number' ? envelope.schemaVersion : undefined,
        provider: typeof envelope.provider === 'string' ? envelope.provider : undefined,
        model: typeof envelope.model === 'string' ? envelope.model : undefined,
        generatedAt: typeof envelope.generatedAt === 'string' ? envelope.generatedAt : undefined,
        reviewDueAt: typeof envelope.reviewDueAt === 'string' ? envelope.reviewDueAt : undefined,
        propertyName: cleanText(envelope.propertyName) || cleanText(envelope.property),
        status: typeof envelope.status === 'string' ? envelope.status : undefined,
        reportHtml: reportHtml || undefined,
        slides,
    }
}

// Named in the failure message so an unexpected envelope can be identified without a debugger.
export const describeEnhancedNarrativeResponse = (response: unknown): string => {
    const value = parseJson(response) ?? response
    if (Array.isArray(value)) return `array of ${value.length}`
    if (!isRecord(value)) return typeof value
    return Object.keys(value).slice(0, 12).join(', ') || 'no keys'
}

const slideOfLayout = <T extends EnhancedNarrativeSlide>(report: EnhancedNarrativeReport, layout: T['layout']) =>
    report.slides.find((slide) => slide.layout === layout) as T | undefined

const readScores = (facts?: EnhancedFactsSlide): MaturityScore[] => {
    const chart = facts?.radarChart
    const dimensions = Array.isArray(chart?.dimensions) ? chart?.dimensions ?? [] : []
    if (dimensions.length) {
        return dimensions.map((dimension, index) => ({
            label: cleanText(dimension.shortLabel) || cleanText(dimension.label) || chart?.labels?.[index] || `Dimension ${index + 1}`,
            score: typeof dimension.score === 'number' && Number.isFinite(dimension.score) ? dimension.score : null,
            evidence: cleanText(dimension.evidence),
        }))
    }
    // Fall back to the parallel labels/series arrays when dimensions are omitted.
    const labels = stringList(chart?.labels)
    const values = chart?.series?.[0]?.values
    return labels.map((label, index) => {
        const score = Array.isArray(values) ? values[index] : null
        return { label, score: typeof score === 'number' && Number.isFinite(score) ? score : null, evidence: '' }
    })
}

const readActionGroup = (group: EnhancedActionSlideGroup | undefined, timeframe: string, focus: string): ActionGroup => ({
    timeframe: cleanText(group?.timeframe) || timeframe,
    focus: cleanText(group?.focus) || focus,
    items: (Array.isArray(group?.actions) ? group?.actions ?? [] : [])
        .map((entry) => ({ title: cleanText(entry?.action), text: cleanText(entry?.rationale) }))
        .filter((entry) => entry.title || entry.text),
})

export const parseEnhancedNarrativeReport = (
    report: EnhancedNarrativeReport,
    propertyName: string,
): EnhancedReportDocument => {
    const facts = slideOfLayout<EnhancedFactsSlide>(report, 'facts-and-radar')
    const issues = slideOfLayout<EnhancedIssuesSlide>(report, 'issues-and-competitors')
    const plan = slideOfLayout<EnhancedActionPlanSlide>(report, 'three-horizon-action-plan')
    if (!facts && !issues && !plan) {
        throw new Error('The AI service returned a report without readable slides. Please try again.')
    }
    const property = cleanText(report.propertyName) || propertyName || 'Property'
    const situation = facts?.currentSituation
    const competitors = (issues?.competitorPositioning?.competitors || [])
        .map((competitor) => [cleanText(competitor?.name), cleanText(competitor?.summary) || cleanText(competitor?.positioning)].filter(Boolean).join(' — '))
        .filter(Boolean)
    const responses = issues?.responses
    const scale = facts?.radarChart?.scale
    const radarMin = typeof scale?.min === 'number' && Number.isFinite(scale.min) ? scale.min : 0
    const radarMax = typeof scale?.max === 'number' && Number.isFinite(scale.max) && scale.max > radarMin ? scale.max : Math.max(5, radarMin + 5)
    const sourceUrls = report.slides.flatMap((slide) => (slide as { sourceUrls?: unknown }).sourceUrls || [])

    return {
        report,
        reportHtml: readReportHtml(report.reportHtml),
        propertyName: property,
        overviewTitle: cleanText(facts?.title) || `${property}: current AI-search position`,
        overviewSubtitle: cleanText(facts?.subtitle),
        maturity: cleanText(situation?.maturity),
        summary: cleanText(situation?.summary),
        strongestAssets: stringList(situation?.strongestAssets),
        primaryRisk: cleanText(situation?.primaryRisk),
        confidenceLevel: cleanText(facts?.confidence?.level),
        confidenceRationale: cleanText(facts?.confidence?.rationale),
        radarTitle: cleanText(facts?.radarChart?.title) || 'Maturity score',
        radarMin,
        radarMax,
        radarStep: typeof scale?.step === 'number' && Number.isFinite(scale.step) && scale.step > 0 ? scale.step : 1,
        radarNote: cleanText(facts?.radarChart?.note),
        scores: readScores(facts).map((score) => ({
            ...score,
            score: score.score !== null && score.score >= radarMin && score.score <= radarMax ? score.score : null,
        })),
        issuesTitle: cleanText(issues?.title) || 'Key issues and competitive positioning',
        issuesSubtitle: cleanText(issues?.subtitle),
        keyIssues: (issues?.keyIssues || [])
            .map((issue) => ({ title: cleanText(issue?.title), text: cleanText(issue?.summary) }))
            .filter((issue) => issue.title || issue.text),
        competitivePositioning: [cleanText(issues?.competitorPositioning?.summary), ...competitors].filter(Boolean).join('\n'),
        responses: [
            { title: 'Strategic response', text: cleanText(responses?.strategic) },
            { title: 'Commercial response', text: cleanText(responses?.commercial) },
            { title: 'Trust response', text: cleanText(responses?.trust) },
        ],
        planTitle: cleanText(plan?.title) || 'Recommended action plan',
        planSubtitle: cleanText(plan?.subtitle),
        actionGroups: [
            readActionGroup(plan?.shortTerm, '0–30 days', 'Short term'),
            readActionGroup(plan?.midTerm, '30–90 days', 'Mid term'),
            readActionGroup(plan?.longTerm, '90+ days', 'Longer term'),
        ],
        targetOutcome: cleanText(plan?.targetOutcome),
        sources: Array.from(new Set([...(Array.isArray(report.sources) ? report.sources : []), ...sourceUrls].filter(safeUrl))),
        speakerNotes: report.slides.map((slide) => ({
            layout: slide.layout,
            title: cleanText(slide.title),
            notes: cleanText(slide.speakerNotes),
            sources: stringList(slide.sourceUrls).filter(safeUrl),
        })),
    }
}
